/**
 * The collections screen's fixed words: how an access level reads, which
 * status filters exist, and the copy around the grid.
 *
 * A collector's answer to "what do I hold, and where" is drawn from these;
 * `collection.model.ts` decides which filters and headings apply to a given
 * list, and `useCollectionController.ts` loads it. The one judgement made here
 * rather than in the model is `STATUS_FILTERS.available` — a filter is
 * offered only when the registry can answer it, and "Sold" cannot be until a
 * transfer exists, so it is declared unavailable rather than left to return
 * everything.
 */

import type { AccessLevel } from "@ondo/shared";

import { WORK_COPY } from "../lib/constants.ts";

/** How a level reads on screen. The stored values are lower-case and terse. */
export const ACCESS_LABELS: Record<AccessLevel, string> = {
  owner: "Yours",
  curator: "Shared with you — you can add works",
  viewer: "Shared with you",
};

/** The short form, for a badge beside a collection name. */
export const ACCESS_BADGES: Record<AccessLevel, string> = {
  owner: "Owner",
  curator: "Curator",
  viewer: "Viewer",
};

/**
 * The status filters, and whether the registry can answer them.
 *
 * `available: false` is not a feature flag on a finished feature — it says the
 * FACT does not exist. `sold` needs a transfer, which is an offer one side
 * makes and the other accepts, and docs/accessFGARules.md lists it under
 * Deliberately absent. A filter that silently returned everything would be
 * worse than no filter at all, and a card defaulting to "In Collection" would
 * be a claim the registry has not made.
 */
export const STATUS_FILTERS = [
  { key: "all", label: "All", available: true, needs: null },
  { key: "displayed", label: "Displayed", available: true, needs: null },
  { key: "on-loan", label: "On Loan", available: true, needs: null },
  { key: "in-storage", label: "In Storage", available: true, needs: null },
  { key: "sold", label: "Sold", available: false, needs: "transfers" },
] as const;

export type StatusFilterKey = (typeof STATUS_FILTERS)[number]["key"];

/**
 * The "show everything" option in the collection picker.
 *
 * Every other option is a collection id, so this has to be something no id can
 * be — a leading `*` is not a uuid.
 */
export const ALL_COLLECTIONS = "*all";

export const COLLECTION_COPY = {
  /** The whole screen, before any collection has arrived. */
  loading: "Loading your collection…",
  /**
   * Shown beside the summary count while a second page is still coming in.
   *
   * Worded as "still" rather than "loading" because the grid underneath it is
   * already populated: the reader is not waiting for the screen, they are
   * waiting for the rest of it, and the count they can see is not yet final.
   */
  partial: "Still counting…",
  collectionLabel: "Collection",
  allCollections: "All collections",
  /** The group heading over the collections the caller owns. */
  yours: "Yours",
  // Read from the shared copy rather than restated: the same words label the
  // owner filter here and an undisclosed holder on a work's own record.
  sharedWithMe: WORK_COPY.sharedWithMe,
  noCollections:
    "No collections yet. A collection is a group of works you hold — you can have as many as you need.",
  noWorks: "Nothing registered here yet.",
  noMatches: "No works match those filters.",
  cannotRegister: "You can view this collection but not add to it.",
  nameRequired: "Give the collection a name.",
  titleRequired: "A work needs a title.",
  artistRequired: "Say who the work is by.",
  ondoIdRequired: "Enter the ONDO id to share with, e.g. ONDO-1042.",
  unknownError: "Something went wrong. Please try again.",
  newCollection: "New collection",
  /**
   * WHO A NEW COLLECTION BELONGS TO — OND-184 and OND-175.
   *
   * Drawn only when the reader holds an admin seat somewhere, so a collector
   * with no firm sees no control. A gallery's stock has to sit in a collection
   * the GALLERY owns, or a dealer leaving takes it with them.
   */
  ownerLabel: "Belongs to",
  /** The first option, and the one every collection made before this had. */
  ownerSelf: "You",
} as const;
